import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorshipService, { MentorshipImportResult } from './MentorshipService';

/**
 * Mentee Pool bulk upload. Same CSV contract as the Mentor Pool upload, so an admin who knows one
 * knows the other. There is no program column: the student's program is read from the system and
 * checked against the campus's eligible programs.
 */
interface MentorshipMenteePoolFileUploadProps {
    show: boolean;
    cohortId: number | null;
    disabled?: boolean;
    instituteId?: number | null;
    onClose: () => void;
    onImported: () => void;
}

const MENTEE_POOL_SAMPLE_CSV = 'institute_id,uuid,email,career_tracks\n';

const downloadMenteePoolSampleCsv = () => {
    const blob = new Blob([MENTEE_POOL_SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mentorship_mentee_pool_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const MentorshipMenteePoolFileUpload = ({
    show,
    cohortId,
    disabled,
    instituteId,
    onClose,
    onImported,
}: MentorshipMenteePoolFileUploadProps) => {
    const [file, setFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [result, setResult] = useState<MentorshipImportResult | null>(null);

    useEffect(() => {
        if (!show) return;
        setFile(null);
        setResult(null);
        setErrorMessage('');
    }, [show]);

    const upload = async () => {
        if (cohortId == null || !file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrorMessage('CSV files only.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            const imported = await MentorshipService.uploadMenteePoolFile(cohortId, file, instituteId);
            setResult(imported);
            if (imported.imported.length > 0) onImported();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const skippedTrackNames = Array.from(
        new Set((result?.imported || []).flatMap((item) => item.skippedTracks || []))
    );

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>File Upload</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <p className="text-muted mb-2">Upload a CSV mentee list. XLSX/XLS is not supported.</p>
                {/* The sample is header-only, so spell out the format the parser expects. */}
                <ul className="text-muted small ps-3 mb-2">
                    <li>
                        <code>institute_id</code> — required, must be a campus in this cohort
                    </li>
                    <li>
                        <code>uuid</code> — required (an <code>email</code> column works instead)
                    </li>
                    <li>
                        <code>career_tracks</code> — required, track names from this cohort&apos;s Domain
                        Taxonomy. For more than one, separate with commas inside quotes:{' '}
                        <code>&quot;Track A,Track B&quot;</code>. Names that are not in the taxonomy are
                        skipped; a row fails only if none of its names match.
                    </li>
                </ul>
                <p className="text-muted small mb-2">
                    The student&apos;s program is taken from the system and must be one of the eligible student
                    programs in Campus Configuration. Mentees are added as <strong>Yet to register</strong>, the
                    same as Add Manually.
                </p>
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mb-3"
                    onClick={downloadMenteePoolSampleCsv}>
                    Download sample CSV
                </button>
                <input
                    type="file"
                    className="form-control"
                    accept=".csv,text/csv"
                    disabled={disabled}
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
                {result && (
                    <div className="mt-3">
                        <div className="alert alert-light border mb-2">
                            Imported {result.imported.length}. Failed {result.failed.length}.
                        </div>
                        {/* Unknown track names are dropped silently otherwise, so name them. */}
                        {skippedTrackNames.length > 0 && (
                            <div className="alert alert-warning">
                                These career tracks are not in this cohort&apos;s Domain Taxonomy and were
                                skipped: {skippedTrackNames.join(', ')}. The mentees were still added with
                                their matching tracks.
                            </div>
                        )}
                        {result.failed.length > 0 && (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Row</th>
                                            <th>Identifier</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.failed.map((item, index) => (
                                            <tr key={`${item.identifier || index}-${item.reason || ''}`}>
                                                <td>{item.row ?? '—'}</td>
                                                <td>{item.identifier || '—'}</td>
                                                <td>{item.reason || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button variant="success" disabled={disabled || isSaving || !file} onClick={upload}>
                    Upload
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MentorshipMenteePoolFileUpload;
